import Cache from "../cache/index";
import { Validation } from "../validation";
import { objectLookup } from "../utils";
/**
 * This is a base handler class that all data handlers should extend.
 * #### Key Features
 * - **Caching**: Built-in cache management with configurable strategies
 * - **Validation**: Field-level and custom validation functions
 * - **SDK Access**: Direct access to MadSDK instance for cross-module operations
 *
 * #### Core Methods:
 * - {@link validate}: Validates an item or specific fields using defined validators
 * - {@link getDependantValidatorKeys}: Override to specify additional validators to run based on a key
 * - {@link getIdentifierKey}: Returns the key used as the unique identifier (default is `"id"`)
 * - {@link getIdentifier}: Retrieves the unique identifier for an item (default uses the key from `getIdentifierKey` to access the item value)
 *
 * #### Validation System
 * The Base handler provides a powerful validation system using validator functions provided on the {@link validators} object.
 * These validators can be synchronous or asynchronous, allowing for complex validation logic including database lookups.
 * All validators are executed via the {@link validate} method, which provides type safe responses based on the provided key(s).
 *
 * @categoryDescription Base Handlers
 * Foundation class for all handlers
 * @showCategories
 *
 * @template ItemType - The type of item this handler will manage.
 * @template CacheType - The type of item this handler will cache.
 * @template Validators - The object of {@link ValidatorMethods} used to validate `ItemType`
 */
export class Base {
    /**
     * Handlers need access to {@link MadSDK}
     * @param {MadSDK} sdk: Instance of MadSDK to use for lookups
     * @param {string} id: the handler class' unique identifier
     * @param {CacheConfig} cachingConfig
     */
    constructor(sdk, id, cachingConfig) {
        this.sdk = sdk;
        this._id = id;
        this.cache = new Cache(this.sdk, this._id, cachingConfig);
    }
    /**
     * Validates an item based on {@link validators} object defined on the extending class.
     * @param item an item to be validated.
     * @param key a key or an array of keys to validate.
     * @param value optional. A value to be validated using the given key validator (to check if it can be used on that key).
     * @return {ValidateReturnType<Validators, Key>}
     * - if key is a string:
     *   - returns `string | undefined` — where the string is an error message if validation fails, or undefined if it passes.
     *   - if key being validated maps to an async function
     *     - returns a `Promise<string | undefined>`
     * - if key is an array:
     *   - returns a {@link Validation} map containing the key-value pairings of errors (if any).
     *   - if any keys being validated map to an async function
     *     - returns a `Promise<Validation>`
     * @examples
     * ```ts
     * // Assuming we have the following item structure:
     * type Thing = {
     *   startDate: Date;
     *   endDate: Date;
     *   parent: { id: string }
     *   id: string;
     * }
     *
     * // single key validation returns the error message if validation fails.
     * const nameError = sdk.things.validate(thing, "name"); // string | undefined
     * // if the validator is async, it returns a promise
     * const parentIdError = await sdk.things.validate(thing, "parent.id"); // Promise<string | undefined>
     *
     * // validator keys that match a field on the item will provide the value to the validators[key] method.
     * const startDateError = sdk.things.validate(thing, "startDate"); // string | undefined
     * // including nested keys:
     * const parentIdError = sdk.things.validate(thing, "parent.id"); // string | undefined
     *
     * // multiple keys validation returns a Validation map of errors (if any).
     * const errors = sdk.things.validate(thing, ["startDate", "endDate", "parent.id"]); // Validation
     * // if any of the validators are async, it returns a promise
     * const asyncErrors = await sdk.things.validate(thing, ["startDate", "endDate", "parent.id"]); // Promise<Validation>
     *
     * // validating without a specific key will validate all keys in the validators object.
     * const allErrors = sdk.things.validate(thing); // Validation
     * const allAsyncErrors = await sdk.things.validate(thing); // Promise<Validation>
     * // this includes any keys that match or do not match fields on the item.
     * const startIsBeforeEnd = allErrors.get("startDateIsBeforeEnd"); // string | undefined
     * ```
     */
    validate(item, key, value) {
        let results = undefined;
        if (typeof this.validators === "undefined") {
            return results;
        }
        const validation = new Validation();
        const promises = [];
        const keys = new Set(typeof key === "string"
            ? [key]
            : Array.isArray(key) && key.length > 0
                ? key.map((k) => k?.toString() || k)
                : Object.keys(this.validators));
        // if we are not validating a specific key, we want to include all dependant keys
        if (typeof key !== "string") {
            keys.forEach((key) => {
                const dependantKeys = this.getDependantValidatorKeys(key);
                dependantKeys.forEach((dependant) => {
                    keys.add(dependant);
                });
            });
        }
        for (const validatorKey of keys) {
            if (typeof validatorKey !== "string") {
                continue;
            }
            const currentValue = objectLookup(item, validatorKey);
            const validator = this.validators[validatorKey];
            if (typeof validator !== "function") {
                continue;
            }
            const updating = typeof value !== "undefined";
            const checking = updating ? value : currentValue;
            const result = validator(item, checking, currentValue, updating);
            if (result instanceof Promise) {
                promises.push(result.then((res) => {
                    if (res?.length) {
                        validation.set(validatorKey, res);
                    }
                }));
            }
            else if (result?.length) {
                validation.set(validatorKey, result);
            }
        }
        if (promises?.length) {
            results = Promise.all(promises).then(() => typeof key === "string" ? validation.get(key) : validation);
        }
        else {
            results = typeof key === "string" ? validation.get(key) : validation;
        }
        return results;
    }
    /**
     * The unique identifier for the extending module.
     */
    get id() {
        return this._id;
    }
    get reconcileCache() {
        return this.cache.reconcile.bind(this.cache);
    }
    /**
     * Method for retrieving the key that uniquely identifies an ItemType object.
     * Defaults to "id".
     * @return the key that unique identifies an ItemType object
     * @examples
     * Override this method to provide a custom identifier key.
     * ```ts
     * type Thing = { customId: string; name: string };
     *
     * class CustomHandler extends Base<Thing> {
     *   getIdentifierKey() {
     *     return "customId";
     *   }
     * }
     * ```
     */
    getIdentifierKey() {
        return "id";
    }
    /**
     * Method for retrieving the unique identifier value from an ItemType object.
     * Defaults to accessing the key returned from {@link getIdentifierKey}.
     * @param item item to retrieve the identifier from.
     * @return the unique identifier value.
     */
    getIdentifier(item) {
        return objectLookup(item, this.getIdentifierKey());
    }
    /**
     * Given a validator key, return the dependant validator keys that should also be ran.
     * On the extending module, override this method to return the specific dependant keys.
     * @param key the key to get dependant keys for.
     * @returns an array of dependant validator keys.
     * @examples
     * When we have validators that should run whenever another validator is triggered,
     * we can use this method to get all the dependant keys. <br/>
     * Let's say we have a validator for "startDate" that also requires "endDate" to be validated.
     * As well as some additional validators that depend on both dates.
     *
     * ```ts
     * class CustomHandler extends Base<Thing> {
     *   // ...
     *
     *   getDependantValidatorKeys(key: keyof MyValidators | keyof Thing): Array<keyof MyValidators>  {
     *     switch(key) {
     *       case "startDate":
     *         return ["endDate", "startDateIsBeforeEnd", "startDateIsInTheFuture"];
     *       case "endDate":
     *         return ["startDate", "startDateIsBeforeEnd"];
     *       default:
     *         return [];
     *     }
     *   }
     *
     * }
     * ```
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getDependantValidatorKeys(key) {
        return [];
    }
}
//# sourceMappingURL=base.js.map